import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { CustomBreadCrumb } from '@/components/customBreadCrumb';
import './style.scss';
import {
  CustomInput,
  CustomPagination,
  CustomSwitch,
  CustomTable,
  PaginationText,
} from '@/components';
import { FileSignature, Info, Shield } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ProductType } from '@/DTO';
import { useAppPagination } from '@/Router/hook';
import { ProductApi } from '@/api';
import { useAppSelector } from '@/redux-config/hooks';
import { selectUser } from '@/redux-config/entities/user';
import { InsideLoading } from '@/components/loading/inside';
import { selectLanguage } from '@/redux-config/entities/language';
import { ConvertDate, ConvertNumber } from '@/utils';
import { SearchComponent } from '@/components/search';

const notFoundImage = require('@/assets/image/notfound.png');

export const ProductList: React.FC<{}> = () => {
  const { t } = useTranslation();

  const crumbs = [
    { name: t('Dashboard'), href: '/' },
    { name: t('Products'), href: '#' },
    { name: t('List'), href: '#' },
  ];

  const navigate = useNavigate();
  const lang = useAppSelector(selectLanguage);
  const user = useAppSelector(selectUser);
  const [loading, setLoading] = useState<boolean>(true);
  const [pagination, setPagination] = useState(useAppPagination());

  const [data, setData] = useState<ProductType[]>([]);
  const [searchData, setSearchData] = useState<string>();

  const handleGetData = (page: number) => {
    setLoading(true);
    ProductApi.GetProductList(
      user,
      { ...pagination, currentPage: page },
      searchData,
    )
      .then((data) => {
        setData(data.data);
        setPagination(data.pagination);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
      });
  };

  useEffect(() => {
    handleGetData(pagination.currentPage);
  }, []);

  const handleChangeStatus = (id: string) => {
    ProductApi.EditProductStatus(user, id)
      .then((item) => {})
      .catch((err) => {});
  };

  return (
    <>
      <Helmet>
        <title>MahdiBookShop | Product List</title>
        <meta name="description" content="Basic example" />
      </Helmet>
      <div className="d-flex flex-column">
        <CustomBreadCrumb breadCrumbList={crumbs} />
        <div className="w-100 p-2">
          <div className="rounded-3 py-2 bg-custom-white h-100 d-flex flex-column justify-content-between">
            <div className={'d-flex flex-row gap-2 align-items-center'}>
              <h5 className={'p-3'}>{t('Products')}</h5>
              <CustomInput
                type={'text_number'}
                name={'search-for-list'}
                placeholder={t('search') + ''}
                className={'custom-input border-reg rounded-5'}
                onChange={setSearchData}
                value={searchData}
              />
              <SearchComponent
                fetchDataApi={() => {
                  if (searchData && searchData?.length > 0) {
                    handleGetData(1);
                  } else {
                    handleGetData(pagination.currentPage);
                  }
                }}
                name={searchData}
              />

              <Link to={'/product/new'} className={'btn btn-success'}>
                {t('New')}
              </Link>
            </div>
            {loading ? (
              <InsideLoading />
            ) : (
              <>
                <CustomTable
                  header={[
                    <span>{t('No')}</span>,
                    <span></span>,
                    <span>{t('Name')}</span>,
                    <span>{t('Size')}</span>,
                    <span>{t('Price')}</span>,
                    <span>{t('amount')}</span>,
                    <span>{t('Date')}</span>,
                    <span>{t('status')}</span>,
                    <span>{t('Options')}</span>,
                  ]}
                  items={[
                    ...data.map((item, key) => [
                      <span>
                        {ConvertNumber(
                          key +
                            1 +
                            (pagination.currentPage - 1) * pagination.perPage +
                            '',
                          lang,
                        )}
                      </span>,
                      <img
                        src={
                          item.content && item.content[0]
                            ? item.content[0].link
                            : notFoundImage
                        }
                        className={'object-fit-contain'}
                        alt={'product image'}
                        width={100}
                        height={100}
                      />,
                      <span>{item.title}</span>,
                      <span>{item.pricing?.size}</span>,
                      <span>{ConvertNumber(item.price + '', lang, true)}</span>,
                      <span>{ConvertNumber(item.amount + '', lang)}</span>,
                      <span>{ConvertDate(item.created_at + '', lang)}</span>,

                      <CustomSwitch
                        defaultChecked={item.status === 'active'}
                        onClick={() => {
                          handleChangeStatus(item.id);
                        }}
                      />,
                      <div
                        className={
                          'd-flex flex-row gap-1 justify-content-center'
                        }
                      >
                        <Link
                          to={`/product/edit/${item.id}`}
                          className={
                            'btn p-1 align-baseline rounded-circle bg-custom-light'
                          }
                        >
                          <FileSignature />
                        </Link>
                        <Link
                          to={`/product/info/${item.id}`}
                          className={
                            'btn p-1 align-baseline rounded-circle bg-custom-light'
                          }
                        >
                          <Info />
                        </Link>
                      </div>,
                    ]),
                  ]}
                />
                <div
                  className={
                    'd-flex flex-row p-3 align-items-center justify-content-between'
                  }
                >
                  <span>
                    <PaginationText pagination={pagination} />
                  </span>
                  <CustomPagination
                    currentPage={pagination.currentPage}
                    perPage={pagination.perPage}
                    changePage={(data: string) => {
                      navigate(`/product?current_page=${data}`);
                      setPagination({ ...pagination, currentPage: +data });
                      handleGetData(+data);
                    }}
                    total={pagination.total}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
